import { Typography } from "@mui/material";
import { ProgressButton, useToast } from "@qubit/autoparts";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import {
  clearSlotBin,
  getSlotBinById,
  getSlottingInfo
} from "~/redux/actions/inventory";

import { selectSelectedSummaries } from "~/redux/selectors/inventorySelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";

import { useGetBinQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { VariantFrontendDto } from "~/types/api";

import { CreateInventoryButton } from "./CreateInventoryButton";

import { NewBinSlotModal } from "./NewBinSlotModal";
import { BinSearchData } from "./getBinIds";
import { useViewType } from "./useViewType";

interface InventorySummaryHeader {
  selectedVariant: VariantFrontendDto | null;
  isAdjustingBins: boolean;
  handleCreateBinInventory: () => void;
  handleFetchAllInventory: () => Promise<void>;
  isAutostoreInventoryView: boolean;
  binNumberParam?: string;
  variantIdParam?: string;
  searchedBinRecord: string[] | null;
  binCreateInventoryDisabled: boolean;
  selectedAutostoreGridId?: Guid;
}

export const InventorySummaryHeader: React.FC<InventorySummaryHeader> = ({
  selectedVariant,
  isAdjustingBins,
  handleCreateBinInventory,
  handleFetchAllInventory,
  isAutostoreInventoryView,
  binNumberParam,
  variantIdParam,
  searchedBinRecord,
  binCreateInventoryDisabled,
  selectedAutostoreGridId
}) => {
  const { t } = useTranslation();
  const { warningToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const dispatch = useAppDispatch();
  const { isBinView, isProductView } = useViewType();
  const slotBin = useAppSelector((state) => state.inventory.slotBin);
  const slottingInfo = useAppSelector((state) => state.inventory.slottingInfo);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const firstSlottingInfo = !!slottingInfo.length && slottingInfo[0];
  const selectedSummaries = useAppSelector(selectSelectedSummaries);
  const [newBinSlotModalOpen, setNewBinSlotModalOpen] = useState(false);
  const [selectedBinSlot, setSelectedBinSlot] = useState<BinSearchData | null>(
    null
  );

  const [isBinOutsideOfGrid, setIsBinOutsideOfGrid] = useState(false);

  const { data: binLocationInformation } = useGetBinQuery(
    selectedAutostoreGridId && binNumberParam
      ? {
          autostoreGridId: selectedAutostoreGridId,
          binNumber: Number(binNumberParam)
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
      skip:
        binNumberParam === undefined || selectedAutostoreGridId === undefined
    }
  );

  const selectedSummariesDontMatchBin =
    !!binNumberParam &&
    selectedSummaries.some(
      (s) => s.autostoreBinNumber !== Number(binNumberParam)
    );

  const hideBinNumberHeader = isAdjustingBins && selectedSummariesDontMatchBin;

  useEffect(() => {
    if (binLocationInformation?.binState) {
      const { binMode, contentCode } = binLocationInformation.binState;
      const flags = binLocationInformation?.flags;

      if (binMode === "X") {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setIsBinOutsideOfGrid(true);
        warningToast(
          t("bin is outside the grid", {
            binNumber: binNumberParam
          })
        );
      } else {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates, react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
        setIsBinOutsideOfGrid(false);
      }

      if (contentCode === 0 || flags?.includes("bin locked")) {
        warningToast(t("bin is locked", { binNumber: binNumberParam || "" }));
      }
    }
  }, [
    isBinOutsideOfGrid,
    dispatch,
    binLocationInformation,
    t,
    binNumberParam,
    warningToast
  ]);

  useEffect(() => {
    void (async () => {
      if (variantIdParam) {
        await dispatch(getSlottingInfo({ variantId: variantIdParam }));
      } else if (
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        binNumberParam &&
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        isAutostoreInventoryView &&
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        searchedBinRecord
      ) {
        await dispatch(
          getSlottingInfo({
            binId: searchedBinRecord[0]
          })
        );
      }
    })();
  }, [
    variantIdParam,
    binNumberParam,
    dispatch,
    isAutostoreInventoryView,
    searchedBinRecord
  ]);

  // get bin data for product slot to display bin location
  useEffect(() => {
    // get bin for most recent/last slot of slotting info
    if (isProductView && firstSlottingInfo) {
      void dispatch(getSlotBinById(firstSlottingInfo.binId));
    } else {
      dispatch(clearSlotBin());
    }
  }, [dispatch, firstSlottingInfo, isProductView]);

  if (isProductView && selectedVariant && !isAdjustingBins) {
    return (
      <>
        <Typography variant="h6" component="div">
          {selectedVariant.productName}
        </Typography>
        <CreateInventoryButton
          handleFetchAllInventory={handleFetchAllInventory}
        />
        <NewBinSlotModal
          open={newBinSlotModalOpen}
          onClose={() => {
            setNewBinSlotModalOpen(false);
          }}
          slotBin={slotBin}
          firstSlottingInfo={firstSlottingInfo || null}
          variantIdParam={variantIdParam}
          selectedBinSlot={selectedBinSlot}
          setSelectedBinSlot={setSelectedBinSlot}
        />
      </>
    );
  } else if (
    isBinView &&
    !hideBinNumberHeader &&
    !!searchedBinRecord &&
    binNumberParam
  ) {
    return (
      <>
        <Typography variant="h6" component="div" marginRight={1}>
          {`Bin ${binNumberParam}`}
        </Typography>
        <ProgressButton
          variant="contained"
          color="primary"
          size="large"
          disabled={
            binCreateInventoryDisabled ||
            !siteWorkstation ||
            isBinOutsideOfGrid ||
            binLocationInformation?.flags.includes("bin locked")
          }
          onClick={() => {
            mixpanelTrack({
              trackedPageName,
              label: "Create Inventory",
              type: "Button Click"
            });
            handleCreateBinInventory();
          }}
        >
          {t("create inventory")}
        </ProgressButton>
      </>
    );
  }
  return null;
};
